/** src/presentation/pages/DashboardPage.tsx */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Header } from '@presentation/components/layout/Header';
import { AppDrawer } from '@presentation/components/layout/AppDrawer/AppDrawer';
import { useAuthContext } from '@presentation/context/AuthContext';
import { routes } from '@presentation/router';

import { Form } from '@/presentation/components/Forms/Form/Form';
import { useSimple } from '@/presentation/hooks/useSimple';
import { useCongrupo } from '@/presentation/hooks/useCongrupo';
import { useEquipo } from '@/presentation/hooks/useEquipo';


export default function NuevoEquipo() {
  const navigate = useNavigate();
  const { user, logout } = useAuthContext();
  const { equipo } = useParams();
  const editing = Boolean(equipo) && equipo !== '0';

  const { data: grupos } = useSimple(
    'luc_ubicaciones_grupo'
  );

  const { data: lotes } = useSimple(
    'luc_lotes'
  );

  const {
    data: ubicaciones,
    loadByGroup,
  } = useCongrupo();

  const { data: tipos } = useSimple(
    'luc_tipo_equipo'
  );

  const {
    Equipo,
    loadById,
    create,
    update
  } = useEquipo();

  const [isDrawerOpen, setIsDrawerOpen] =
    useState(false);


  useEffect(() => {
    if (editing && equipo) {
      loadById(equipo);
    }
  }, [editing, equipo]);

  const tipoOptions = tipos.map((tipo) => ({
    value: tipo.id,
    label: tipo.nombre,
  }));

  const grupoOptions = grupos.map((grupo) => ({
    value: grupo.id,
    label: grupo.nombre,
  }));

  const loteOptions = lotes.map((lote) => ({
    value: lote.id,
    label: lote.nombre,
  }));

  const ubicacionOptions = useMemo(() => {
    const options = ubicaciones.map((equipo) => ({
      value: equipo.id,
      label: equipo.nombre,
    }));

    if (
      editing &&
      Equipo?.id_ubicacion &&
      !options.some(
        (option) => option.value === Equipo?.id_ubicacion
      )
    ) {
      options.unshift({
        value: Equipo?.id_ubicacion,
        label: Equipo?.ubicacion,
      });
    }

    return options;
  }, [editing, ubicaciones, Equipo?.id_ubicacion]);

  const initialValues = useMemo(
    () =>
      editing && Equipo
        ? {
          equipo: Equipo.equipo,
          tipo: Equipo.id_tipo,
          lote: Equipo.id_lote,
          grupo: Equipo.grupo,
          ubicacion: Equipo.id_ubicacion,
        }
        : {},
    [editing, Equipo]
  );

  if (editing && !Equipo) {
    return <>Cargando equipo...</>;
  }

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    setIsDrawerOpen(false);
    navigate(routes.login, { replace: true });
  };


  const handleSubmit = async (
    values: Record<string, unknown>
  ) => {
    const nuevoEquipo = {
      equipo: String(values.equipo),
      tipo: String(values.tipo),
      lote: String(values.lote),
      ubicacion: String(values.ubicacion),
      usuario: String(user?.id ?? 0),
    };

    const equipoGuardado = editing && Equipo
      ? await update(Equipo.id.toString(), nuevoEquipo)
      : await create(nuevoEquipo);

    navigate(`/equipo/${equipoGuardado.id}`, { replace: true });
  };

  return (
    <div>
      <Header
        title={
          editing
            ? 'Editar Equipo'
            : 'Nuevo Equipo'
        }
        onMenuClick={handleOpenDrawer}
      />

      <AppDrawer
        isOpen={isDrawerOpen}
        user={user}
        onClose={handleCloseDrawer}
        onLogout={handleLogout}
      />

      <main style={{ padding: '24px' }}>
        <Form
          fields={[
            {
              name: 'equipo',
              label: 'Equipo',
              type: 'text',
            },
            {
              name: 'lote',
              label: 'Lote',
              type: 'select',
              options: loteOptions,
            },
            {
              name: 'tipo',
              label: 'Tipo de Equipo',
              type: 'select',
              options: tipoOptions,
            },
            {
              name: 'grupo',
              label: 'Grupo',
              type: 'select',
              options: grupoOptions,
            },
            {
              name: 'ubicacion',
              label: 'Ubicación',
              type: 'select',
              options: ubicacionOptions,
            },
          ]}
          onFieldChange={(name, value) => {
            if (name === 'grupo') {
              loadByGroup(
                'luc_ubicaciones',
                String(value)
              );
            }
          }}
          initialValues={initialValues}
          submitLabel={
            editing
              ? 'Guardar cambios'
              : 'Crear equipo'
          }
          onSubmit={handleSubmit}
        />
      </main>
    </div>
  );
}
